import React, { useRef, useEffect, useState } from 'react';
import { ExternalLinkModal } from './ExternalLinkModal';

interface LinkClickHandlerProps {
  htmlContent: string;
  className?: string;
}

export const LinkClickHandler: React.FC<LinkClickHandlerProps> = ({
  htmlContent,
  className = ''
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [linkModal, setLinkModal] = useState<{ isOpen: boolean; url: string }>({
    isOpen: false,
    url: ''
  });

  // Set innerHTML imperatively so React never tracks these child nodes.
  // This prevents NotFoundError when browser extensions modify the DOM.
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = htmlContent;
    }
  }, [htmlContent]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if the clicked element is a link or inside a link
      const link = target.closest('a');

      if (link && link.href) {
        e.preventDefault();
        e.stopPropagation();

        // Show confirmation modal
        setLinkModal({
          isOpen: true,
          url: link.href
        });
      }
    };

    const element = contentRef.current;
    if (element) {
      element.addEventListener('click', handleClick);
    }

    return () => {
      if (element) {
        element.removeEventListener('click', handleClick);
      }
    };
  }, [htmlContent]);

  const handleConfirm = () => {
    window.open(linkModal.url, '_blank', 'noopener,noreferrer');
    setLinkModal({ isOpen: false, url: '' });
  };

  const handleCancel = () => {
    setLinkModal({ isOpen: false, url: '' });
  };

  return (
    <>
      <div
        ref={contentRef}
        className={className}
      />
      <ExternalLinkModal
        isOpen={linkModal.isOpen}
        url={linkModal.url}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};
